import {
  ContentComment,
  Course,
  CourseCertificate,
  FilterCourse,
  Instructor,
  User,
  UserCourse
} from "../../../../domain/entities/entities";
import { InterfaceCourseUseCases } from "../../../../domain/usecase/impl/courseUseCases";
import { ActionContext } from "vuex";
import { ListResponse } from "../../../../domain/entities/shop/product/listResponse";
import { InterfaceEventUseCases } from "../../../../domain/usecase/impl/event/eventUseCases";
import { Content } from "../../../presentation/views/content/helpers";
import courseUseCases from "../../../../domain/usecase/content_provider/course/content_repository";
import { Order } from "../../../../domain/entities/order";

class CourseState {
  courses: Course[] = [];
  course: Course = new Course();
  recommendedCourses: Course[] = [];
  userCourseSituation: UserCourse = new UserCourse();
  certificate: CourseCertificate = new CourseCertificate();
  /** State that controls loading.*/
  loading = false;
  coursesByCourseTrack: Course[] = [];
  coursesPages = 0;
  currentFilter = new FilterCourse();
  isCommentsModalOpen = false;
  comments: ContentComment[] = [];
  commentsTotalCount = 0;
  commentsPage = 1;
  newCourses: Course[] = [];
  contents: Content[] = [];
  instructors: Instructor[] = [];
  coursesByCourseTrackPage = 0;
  userCourses: Course[] = [];
  indicationCourses: Course[] = [];
  indicationUsers: User[] = [];
  favoritesTrainings: Course[] = [];
  numberOfPagesFavorite = 1;
}

const state = new CourseState();

const mutations = {
  LIST_COURSES(state: CourseState, courses: Course[]): void {
    state.courses = courses;
  },
  LIST_COURSES_OF_COURSES_TRACKS(
    state: CourseState,
    list: ListResponse<Course>
  ): void {
    state.coursesByCourseTrack = list.items;
    state.coursesByCourseTrackPage = list.page;
  },

  LOAD_RECOMMENDED_COURSES(
    state: CourseState,
    recommendedCourses: Course[]
  ): void {
    state.recommendedCourses = recommendedCourses;
  },

  GET_COURSE(state: CourseState, course: Course): void {
    state.course = course;
  },

  ENROLL_IN_COURSE(state: CourseState): void {
    state.userCourseSituation.enrolled = true;
  },

  SET_USER_COURSE_SITUATION(
    state: CourseState,
    userCourseSituation: UserCourse
  ): void {
    state.userCourseSituation = userCourseSituation;
  },

  LIST_NEWS_COURSES(state: CourseState, newCourses: Course[]): void {
    state.newCourses = newCourses;
  },

  /** Mutation that sets the loading state.
   * @param state
   * @param isVisible parameter that takes a boolean variable.*/
  SET_LOADING(state: CourseState, isVisible: boolean): void {
    state.loading = isVisible;
  },

  SET_DECOLA_CERTIFICATE: (
    state: CourseState,
    certificate: CourseCertificate
  ): void => {
    state.certificate = certificate;
  },
  SET_COURSES_PAGES(state: CourseState, page: number): void {
    state.coursesPages = page;
  },
  SET_CURRENT_FILTER(state: CourseState, filter: FilterCourse): void {
    state.currentFilter = filter;
  },

  OPEN_COMMENTS_MODAL(state: CourseState): void {
    state.isCommentsModalOpen = true;
  },

  CLOSE_COMMENTS_MODAL(state: CourseState): void {
    state.isCommentsModalOpen = false;
  },
  CLEAR_COURSE_COMMENTS(state: CourseState): void {
    state.comments = [];
  },
  LOAD_COMMENTS(
    state: CourseState,
    comments: ListResponse<ContentComment>
  ): void {
    state.comments = comments.items;
    state.commentsPage = comments.page;
    state.commentsTotalCount = comments.totalCount;
  },
  SET_CONTENTS(state: CourseState, contents: Content[]) {
    state.contents = contents;
  },
  SET_INSTRUCTORS(state: CourseState, instructors: Instructor[]) {
    state.instructors = instructors;
  },
  REMOVE_COURSE_FROM_USER_LIST(state: CourseState, courseID: number) {
    state.userCourses = state.userCourses.filter(
      course => course.id !== courseID
    );
  },
  LIST_INDICATIONS(state: CourseState, indicationCourses: Course[]): void {
    state.indicationCourses = indicationCourses;
  },
  GET_INDICATION_USERS(state: CourseState, indicationUsers: User[]): void {
    state.indicationUsers = indicationUsers;
  },
  ADD_FAVORITE_TRAINING(state: CourseState): void {
    state.isCommentsModalOpen = false;
  },
  REMOVE_FAVORITE_TRAINING(state: CourseState): void {
    state.isCommentsModalOpen = false;
  },
  LIST_FAVORITE_TRAININGS(
    state: CourseState,
    indicationCourses: Course[]
  ): void {
    state.favoritesTrainings = indicationCourses;
  },
  SET_NUMBER_OF_PAGES_FAVORITE(state: CourseState, numberOfPagesFavorite: number): void {
    state.numberOfPagesFavorite = numberOfPagesFavorite;
  },
};

const actions = {
  async loadCourses(
    { commit }: ActionContext<CourseState, CourseState>,
    payload: {  order: Order; filter: FilterCourse }
  ): Promise<void> {
    let requestFilter: FilterCourse = new FilterCourse();
    if (payload.filter) {
      requestFilter = payload.filter;
    }
    const response: ListResponse<Course> = await InterfaceCourseUseCases.list(
      payload.order,
      requestFilter
    );

    commit("LIST_COURSES", response.items);
    commit("SET_COURSES_PAGES", response.page);
  },

  async loadNewsCourses({
    commit
  }: ActionContext<CourseState, CourseState>): Promise<void> {
    const response = await InterfaceCourseUseCases.listNewCourses();
    commit("LIST_NEWS_COURSES", response);
  },

  async loadCoursesOfCourseTrack(
    { commit }: ActionContext<CourseState, CourseState>,
    payload: { order: Order; courseTrackID: number }
  ): Promise<Course[]> {
    const response: ListResponse<Course> = await InterfaceCourseUseCases.listByCourseTrack(
      payload.order,
      payload.courseTrackID
    );
    commit("LIST_COURSES_OF_COURSES_TRACKS", response);
    return response.items;
  },

  async loadCourse(
    { commit }: ActionContext<CourseState, CourseState>,
    course_id: number
  ): Promise<Course> {
    const course: Course = await InterfaceCourseUseCases.get(course_id);
    commit("GET_COURSE", course);
    return course;
  },

  async loadRecommendedCourses({
    commit
  }: ActionContext<CourseState, CourseState>): Promise<void> {
    const courses = await InterfaceCourseUseCases.listRecommendedCourses();
    commit("LOAD_RECOMMENDED_COURSES", courses);
  },

  setCurrentCourse(
    { commit }: ActionContext<CourseState, CourseState>,
    course
  ): void {
    commit("GET_COURSE", course);
  },

  async enrollInCourse(
    { commit }: ActionContext<CourseState, CourseState>,
    course_id: number
  ): Promise<void> {
    await InterfaceCourseUseCases.enrollInCourse(course_id);
    commit("ENROLL_IN_COURSE");
  },

  async userCourseSituation(
    { commit }: ActionContext<CourseState, CourseState>,
    course_id: number
  ): Promise<void> {
    const userCourseSituation: UserCourse = await InterfaceCourseUseCases.userCourseSituation(
      course_id
    );
    commit("SET_USER_COURSE_SITUATION", userCourseSituation);
  },

  /** Get the generated certificate from backend and commit on the certificate state. */
  loadCertificate: async (
    { commit }: ActionContext<CourseState, CourseState>,
    courseID: number
  ) => {
    const certificate = await InterfaceCourseUseCases.getCertificate(courseID);
    commit("SET_DECOLA_CERTIFICATE", certificate);
  },

  /** Action used to wait for completion of the end course request
   * @param commit
   * @param isVisible parameter that takes a boolean variable. */
  setLoading(
    { commit }: ActionContext<CourseState, CourseState>,
    isVisible: boolean
  ): void {
    commit("SET_LOADING", isVisible);
  },

  currentFilter(
    { commit }: ActionContext<CourseState, CourseState>,
    filter: FilterCourse
  ): void {
    commit("SET_CURRENT_FILTER", filter);
  },
  openCommentsModal({ commit }: ActionContext<CourseState, CourseState>): void {
    commit("OPEN_COMMENTS_MODAL");
  },
  closeCommentsModal({
    commit
  }: ActionContext<CourseState, CourseState>): void {
    commit("CLOSE_COMMENTS_MODAL");
  },
  clearComments({ commit }: ActionContext<CourseState, CourseState>): void {
    commit("CLEAR_COMMENTS");
  },
  async loadContentCommentsById(
    { commit }: ActionContext<CourseState, CourseState>,
    payload: { courseID: number; order: Order }
  ): Promise<void> {
    const comments: ListResponse<ContentComment> = await InterfaceCourseUseCases.getComments(
      payload.courseID,
      payload.order
    );
    commit("LOAD_COMMENTS", comments);
  },
  async loadEventCommentsById(
    { commit }: ActionContext<CourseState, CourseState>,
    eventId: number
  ): Promise<void> {
    const comments: ContentComment[] = await InterfaceEventUseCases.getComments(
      eventId
    );
    commit("LOAD_COMMENTS", comments);
  },

  async loadContents(
    { commit }: ActionContext<CourseState, CourseState>,
    courseID: number
  ): Promise<void> {
    const contents = await courseUseCases.getAllContents(courseID);
    commit("SET_CONTENTS", contents);
  },

  async loadInstructors(
    { commit }: ActionContext<CourseState, CourseState>,
    courseID: number
  ): Promise<void> {
    const response = await InterfaceCourseUseCases.getInstructors(courseID);
    commit("SET_INSTRUCTORS", response);
  },

  async leaveCourse(
    { commit }: ActionContext<CourseState, CourseState>,
    courseID: number
  ): Promise<void> {
    try {
      await InterfaceCourseUseCases.leaveCourse(courseID);

      commit("REMOVE_COURSE_FROM_USER_LIST", courseID);
    } catch (e) {
      console.log(e);
      throw e;
    }
  },

  async listIndicationsCourses({
    commit
  }: ActionContext<CourseState, CourseState>): Promise<void> {
    try {
      const response: ListResponse<Course[]> = await InterfaceCourseUseCases.listIndicationsCourses();
      commit("LIST_INDICATIONS", response.items);
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  async loadIndicationByUsers(
    { commit }: ActionContext<CourseState, CourseState>,
    courseID: number
  ): Promise<void> {
    try {
      const response: ListResponse<User[]> = await InterfaceCourseUseCases.loadIndicationByUsers(
        courseID
      );
      commit("GET_INDICATION_USERS", response.items);
    } catch (e) {
      console.log(e);
      throw e;
    }
  },

  async indicationCourse(
    { commit }: ActionContext<CourseState, CourseState>,
    courseID: number
  ): Promise<void> {
    try {
      await InterfaceCourseUseCases.indicationCourse(courseID);

      commit("SET_INDICATION", courseID);
    } catch (e) {
      console.log(e);
      throw e;
    }
  },

  async addFavoriteTraining(
    { commit }: ActionContext<CourseState, CourseState>,
    courseID: number
  ): Promise<void> {
    try {
      await InterfaceCourseUseCases.addFavoriteTraining(courseID);

      commit("ADD_FAVORITE_TRAINING", courseID);
    } catch (e) {
      console.log(e);
      throw e;
    }
  },

  async removeFavoriteTraining(
    { commit }: ActionContext<CourseState, CourseState>,
    courseID: number
  ): Promise<void> {
    try {
      await InterfaceCourseUseCases.removeFavoriteTraining(courseID);

      commit("REMOVE_FAVORITE_TRAINING", courseID);
    } catch (e) {
      console.log(e);
      throw e;
    }
  },

  async listFavoriteTrainings(
    { commit }: ActionContext<CourseState, CourseState>,
    order: Order
  ): Promise<void> {
    try {
      const response: ListResponse<Course[]> = await InterfaceCourseUseCases.listFavoriteTrainings(
        order
      );
      commit("LIST_FAVORITE_TRAININGS", response.items);
      commit("SET_NUMBER_OF_PAGES_FAVORITE", response.page);
    } catch (e) {
      console.error(e);
      throw e;
    }
  }
};

const getters = {
  course: (state: CourseState): Course => state.course,
  newCourses: (state: CourseState): Course[] => state.newCourses,
  coursesByCourseTrack: (state: CourseState): Course[] =>
    state.coursesByCourseTrack,
  coursesByCourseTrackPage: (state: CourseState): number =>
    state.coursesByCourseTrackPage,
  courses: (state: CourseState) => state.courses,
  coursesPages: (state: CourseState): number => state.coursesPages,
  recommendedCourses: (state: CourseState) => state.recommendedCourses,
  userCourseSituation: (state: CourseState): UserCourse =>
    state.userCourseSituation,
  certificate: (state: CourseState): CourseCertificate =>
    state.certificate || new CourseCertificate(),
  /** Loading state getter. */
  loading: (state: CourseState): boolean => state.loading,
  currentFilter: (state: CourseState): FilterCourse => state.currentFilter,
  isCommentsModalOpen: (state: CourseState): boolean =>
    state.isCommentsModalOpen,
  comments: (state: CourseState): ContentComment[] => state.comments,
  commentsPage: (state: CourseState): number => state.commentsPage,
  commentsTotalCount: (state: CourseState): number => state.commentsTotalCount,
  contents: (state: CourseState): Content[] => state.contents,
  instructors: (state: CourseState): Instructor[] => state.instructors,
  indicationCourses: (state: CourseState): Course[] => state.indicationCourses,
  indicationUsers: (state: CourseState): User[] => state.indicationUsers,
  favoritesTrainings: (state: CourseState): Course[] => state.favoritesTrainings,
  numberOfPagesFavorite: (state: CourseState): number => state.numberOfPagesFavorite,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
